const URL = '/track';

export function useClickID() {
  const { $addBreadCrumbs, $db } = useNuxtApp();
  const loading = ref(true);
  const store = useKegelStore();

  const parseBool = (value: any, defaultValue: boolean): boolean => {
    return (value === 'true' || value === 'false' || typeof value === 'boolean')
      ? JSON.parse(value)
      : defaultValue;
  };

  const getCookie = (name: string): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
    { return parts.pop()?.split(';').shift(); }
  };

  const setCookie = (clickID: string, domain: string, duration: number) => {
    const cookieName = 'rtkclickid-store';
    const cookieValue = clickID;
    const expirationTime = 86400 * duration * 1000;
    const date = new Date();
    date.setTime(date.getTime() + expirationTime);
    const expires = date.toUTCString();
    document.cookie = `${cookieName}=${cookieValue}; expires=${expires}; path=/; domain=${domain}`;
  };

  const setSessionClickID = (clickID: string) => {
    sessionStorage.setItem('rtkclickid', clickID);
  };

  const trackView = async (clickID: string, referrer: string) => {
    const viewUrl = `${URL}/view?clickid=${clickID}&referrer=${referrer}`;
    try {
      await fetch(viewUrl);
    } catch (error: unknown) {
      console.error('Click id track view ERROR', error);
    }
  };

  const removeParam = (key: string, sourceURL: string): string => {
    const [urlBase, queryString] = sourceURL.split('?');
    if (!queryString)
    { return sourceURL; }
    const params = queryString
      .split('&')
      .filter(param => !param.startsWith(`${key}=`))
      .join('&');
    return `${urlBase}?${params}`;
  };

  const fetchData = async (url: string) => {
    const response = await fetch(url)
      .then((response: Response) => {
        if (!response.ok) {
          console.error('CLICK ID ERROR response status', response.status, 'header', response.headers);
          return response.json().then((error) => {
            throw new Error(JSON.stringify(error));
          });
        }
        if (response.status === 204 || response.headers.get('Content-Length') === '0') {
          return {};
        }
        return response.json();
      }).then((data: any) => {
        return data;
      });

    $addBreadCrumbs({
      category: 'clickID',
      message: 'clickid request sent',
      level: 'debug',
    });

    return response;
  };

  const getClickID = async (experimentId: ComputedRef<string | undefined>, variation: ComputedRef<string | undefined>): Promise<string | undefined> => {
    const campaignIds = { default: '6569e30566e3190001ccb376' };
    const cookieDomain = window.location.hostname;
    const cookieDuration = Number.parseInt('90') || 30;
    const registerViewOncePerSession = parseBool('false', false);

    let lastPaidClickAttribution = false;
    let firstClickAttribution = false;
    const attribution = 'lastpaid';
    const referrer = document.referrer;

    if (attribution === 'lastpaid') {
      lastPaidClickAttribution = true;
    } else if (attribution === 'firstclick') {
      firstClickAttribution = true;
    }

    const ourCookie = getCookie('rtkclickid-store');
    let clickID = '';

    const urlParams = new URLSearchParams(window.location.search);
    const rtkfbp = getCookie('_fbp');
    const rtkfbc = getCookie('_fbc');
    const rtkttclid = getCookie('ttclid');
    const pixelParams = `&${window.location.search.substring(1)}&sub19=${rtkfbp}&sub20=${rtkfbc}`;

    if (rtkfbp) {
      localStorage.setItem('fbp', rtkfbp);
    }
    if (rtkfbc) {
      localStorage.setItem('fbc', rtkfbc);
    }
    if (rtkttclid) {
      localStorage.setItem('ttclid', rtkttclid);
    }

    const campaignID = urlParams.get('cmpid') || campaignIds.default;

    if (lastPaidClickAttribution && campaignID === campaignIds.default) {
      firstClickAttribution = true;
    }

    let initialSrc = `${URL}/${campaignID}?format=json&referrer=`; // ${referrer}`;
    if (experimentId.value && variation.value) {
      initialSrc = `${initialSrc}&experiment=${experimentId.value}&variation=${variation.value}`;
    }

    for (let i = 1; i <= 10; i++) {
      initialSrc = removeParam(`sub${i}`, initialSrc);
    }
    initialSrc = removeParam('cost', initialSrc);
    initialSrc = removeParam('ref_id', initialSrc);

    if (!urlParams.get('rtkcid')) {
      if (!sessionStorage.getItem('rtkclickid') || sessionStorage.getItem('rtkclickid') === 'undefined') {
        try {
          const rawData = await fetchData(initialSrc + pixelParams);
          clickID = rawData.clickid;

          $addBreadCrumbs({
            category: 'clickID',
            message: `set clickID (empty store)${clickID}`,
            level: 'debug',
          });

          setSessionClickID(clickID as string);

          if (!ourCookie || !firstClickAttribution) {
            setCookie(clickID as string, cookieDomain, cookieDuration);
          }

          try {
            if (sessionStorage.getItem('viewOnce') !== '1') {
              await trackView(rawData.clickid, referrer);
            }
          } catch (error: unknown) {
            console.error('CLICK ID TRACK VIEW ERROR', error);
          }

          try {
            if (registerViewOncePerSession) {
              sessionStorage.setItem('viewOnce', '1');
            }
          } catch (error: unknown) {
            console.error('CLICK ID SESSION STORAGE ERROR', error);
          }

          try {
            loading.value = false;
            const clickTime = String(Math.floor(Date.now() / 1000));
            $db.setItem('b_click_time', clickTime);
            localStorage.setItem('b_click_time', clickTime);
            store.clickTime = clickTime;
          } catch (error: unknown) {
            console.error('CLICK ID VARIABLE INITIALIZATION ERROR', error);
          }

          return clickID as string;
        } catch (error) {
          $addBreadCrumbs({
            category: 'clickID',
            message: `error ${error}`,
            level: 'debug',
          });
          console.error('CLICK ID', error);
        }
      } else {
        clickID = sessionStorage.getItem('rtkclickid')!;
        loading.value = false;

        if (!ourCookie || !firstClickAttribution) {
          setCookie(clickID as string, cookieDomain, cookieDuration);
        }

        if (sessionStorage.getItem('viewOnce') !== '1') {
          await trackView(clickID, referrer);
        }

        if (registerViewOncePerSession) {
          sessionStorage.setItem('viewOnce', '1');
        }

        return clickID as string;
      }
    } else {
      clickID = urlParams.get('rtkcid')!;
      loading.value = false;

      if (!ourCookie || !firstClickAttribution) {
        setCookie(clickID, cookieDomain, cookieDuration);
      }

      if (sessionStorage.getItem('viewOnce') !== '1') {
        await trackView(clickID, referrer);
      }

      setSessionClickID(clickID);

      return clickID as string;
    }
    return clickID as string;
  };

  return {
    loading,
    getClickID,
  };
}
